import { useMemo, useState } from 'react';
import { CalendarDays, ChevronDown } from 'lucide-react';

/**
 * Shared date-range filter (#11) — one control for every list that has a date,
 * rather than a different one per page.
 *
 * Presets are the locked decision: Today · Last 7 days · Last 30 days · This
 * month · Custom, plus "All time" as the default so no list silently hides rows
 * the moment this ships.
 *
 * **Everything here works in LOCAL calendar days, deliberately.** An admin asking
 * for "today" means today where they are, not a UTC window that starts at 6am
 * Dhaka time. Rows carry either a bare `YYYY-MM-DD` or a full ISO timestamp, so
 * both are reduced to a local calendar day before comparing, and the range is
 * INCLUSIVE at both ends — "Last 7 days" that quietly excluded today would be
 * the kind of off-by-one nobody notices until a number is wrong in a meeting.
 */

export type DatePresetKey = 'all' | 'today' | 'last7' | 'last30' | 'month' | 'custom';

export interface DateRange {
  preset: DatePresetKey;
  /** Inclusive local calendar days, `YYYY-MM-DD`. Null = unbounded on that side. */
  from: string | null;
  to: string | null;
}

export const ALL_TIME: DateRange = { preset: 'all', from: null, to: null };

const PRESET_LABEL: Record<DatePresetKey, string> = {
  all: 'All time',
  today: 'Today',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  month: 'This month',
  custom: 'Custom',
};

/** Local calendar day of a Date, as `YYYY-MM-DD` — never via toISOString(), which is UTC. */
function localDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDay(d);
}

/** Resolve a preset to concrete inclusive bounds, evaluated at click time. */
export function resolvePreset(preset: DatePresetKey): DateRange {
  const today = localDay(new Date());
  switch (preset) {
    case 'today':
      return { preset, from: today, to: today };
    // "Last 7 days" includes today — 6 days back plus today = 7 calendar days.
    case 'last7':
      return { preset, from: daysAgo(6), to: today };
    case 'last30':
      return { preset, from: daysAgo(29), to: today };
    case 'month': {
      const d = new Date();
      return { preset, from: localDay(new Date(d.getFullYear(), d.getMonth(), 1)), to: today };
    }
    case 'custom':
      return { preset, from: null, to: null };
    default:
      return ALL_TIME;
  }
}

/**
 * Does a row's date fall inside the range?
 *
 * Accepts `YYYY-MM-DD`, a full ISO timestamp, or null. A row with NO date is kept
 * only when the range is unbounded — an unpaid invoice has no paid-date, and
 * silently dropping it from "Last 30 days" would make the list look shorter than
 * it is without saying why.
 */
export function inRange(value: string | null | undefined, range: DateRange): boolean {
  if (range.from == null && range.to == null) return true;
  if (!value) return false;
  // A timestamp's local day, not its UTC day: `2026-08-01T02:00:00Z` is still
  // 1 August in UTC but already 1 August 08:00 in Dhaka — and for a value like
  // `2026-07-31T20:00:00Z` the two disagree, which is exactly the boundary case.
  const day = value.length > 10 ? localDay(new Date(value)) : value.slice(0, 10);
  if (range.from && day < range.from) return false;
  if (range.to && day > range.to) return false;
  return true;
}

/** Short label for the chip, e.g. "Last 7 days" or "1 Aug – 15 Aug". */
function chipLabel(range: DateRange): string {
  if (range.preset !== 'custom') return PRESET_LABEL[range.preset];
  if (!range.from && !range.to) return 'Custom';
  const fmt = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  if (range.from && range.to) return `${fmt(range.from)} – ${fmt(range.to)}`;
  return range.from ? `From ${fmt(range.from)}` : `Until ${fmt(range.to!)}`;
}

/**
 * The control. Mirrors the FilterChip pattern the admin lists already use, so it
 * sits in the same filter bar without looking bolted on.
 */
export function DateRangeFilter({
  label = 'Date',
  value,
  onChange,
}: {
  label?: string;
  value: DateRange;
  onChange: (r: DateRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = value.preset !== 'all';
  const summary = useMemo(() => chipLabel(value), [value]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition ' +
          (active
            ? 'border-brand bg-brand-subtle text-brand dark:bg-brand/15 dark:text-brand-dm dark:border-brand-dm'
            : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800')
        }
      >
        <CalendarDays className="w-3.5 h-3.5" />
        <span className="text-zinc-500 dark:text-zinc-400">{label}</span>
        <span className="font-medium">{summary}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <>
          {/* Click-away. A plain overlay rather than a document listener so it
              cannot leak past unmount. */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute z-40 mt-1 w-64 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg p-1.5">
            {(['all', 'today', 'last7', 'last30', 'month'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => { onChange(resolvePreset(k)); setOpen(false); }}
                className={
                  'block w-full text-left px-3 py-1.5 rounded-md text-sm transition ' +
                  (value.preset === k
                    ? 'bg-brand-subtle text-brand dark:bg-brand/15 dark:text-brand-dm font-medium'
                    : 'text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800')
                }
              >
                {PRESET_LABEL[k]}
              </button>
            ))}

            <div className="mt-1.5 pt-1.5 border-t border-zinc-200 dark:border-zinc-700 px-2 pb-1">
              <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">Custom range</div>
              <div className="flex items-center gap-1.5">
                <input
                  type="date"
                  value={value.from ?? ''}
                  max={value.to ?? undefined}
                  onChange={(e) => onChange({ preset: 'custom', from: e.target.value || null, to: value.to })}
                  className="flex-1 min-w-0 px-2 py-1 text-xs rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700"
                  aria-label="From date"
                />
                <span className="text-xs text-zinc-400">–</span>
                <input
                  type="date"
                  value={value.to ?? ''}
                  min={value.from ?? undefined}
                  onChange={(e) => onChange({ preset: 'custom', from: value.from, to: e.target.value || null })}
                  className="flex-1 min-w-0 px-2 py-1 text-xs rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700"
                  aria-label="To date"
                />
              </div>
              {active && (
                <button
                  type="button"
                  onClick={() => { onChange(ALL_TIME); setOpen(false); }}
                  className="mt-2 text-xs text-brand dark:text-brand-dm hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
